import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Base64 } from 'js-base64';
import {
  DataService,
  DropdownStatusService,
  FromStatusService,
  PassengerService,
  SearchUrlService,
  StateServiceService
} from '../../services';
import { OpenpanelService } from '../../services/openpanel.service';
import { FaroService } from '../../services/faro.service';

export interface FlightSearchAirport {
  name: string;
  city: string;
  country: string;
  iata: string;
}

export interface FlightSearchPassengers {
  adults: number;
  children: number;
  infants: number;
  travelClass: string;
}

export interface PopularRoute {
  label: string;
  from: FlightSearchAirport;
  to: FlightSearchAirport;
}

/** The search payload shape shared by the home and booking pages (Base64-encoded into `searchFlights`). */
export interface FlightSearchPayload {
  depature_city: string;
  destination_city: string;
  departure_on: string;
  arrival_on: string;
  trip_type: 'oneway' | 'return';
  adult_count: number;
  child_count: number;
  infant_count: number;
  travel_class: string;
  from_data: FlightSearchAirport;
  destination_data: FlightSearchAirport;
  returning: boolean;
}

const NAIROBI: FlightSearchAirport = { name: 'Jomo Kenyatta International', city: 'Nairobi', country: 'Kenya', iata: 'NBO' };

export const POPULAR_ROUTES: PopularRoute[] = [
  { label: 'Mombasa', from: NAIROBI, to: { name: 'Mombasa Moi International Airport', city: 'Mombasa', country: 'Kenya', iata: 'MBA' } },
  { label: 'Kisumu', from: NAIROBI, to: { name: 'Kisumu Airport', city: 'Kisumu', country: 'Kenya', iata: 'KIS' } },
  { label: 'Diani', from: NAIROBI, to: { name: 'Ukunda Airstrip', city: 'Ukunda', country: 'Kenya', iata: 'UKA' } },
  { label: 'Eldoret', from: NAIROBI, to: { name: 'Eldoret International Airport', city: 'Eldoret', country: 'Kenya', iata: 'EDL' } },
  { label: 'London', from: NAIROBI, to: { name: 'London Heathrow Airport', city: 'London', country: 'United Kingdom', iata: 'LHR' } }
];

const CABIN_LABELS: Record<string, string> = {
  economy: 'Economy',
  premiumeconomy: 'Premium Economy',
  business: 'Business',
  first: 'First Class',
  firstclass: 'First Class'
};

const LOCALE = 'en';

/**
 * Set while a mobile full-screen picker is open. When the results page is
 * re-created on the way back, the picker's fresh value (held in the shared
 * mobile channels) must win over the page's stored search. Cleared on submit.
 */
const PICKER_PENDING_KEY = 'flightSearchPickerPending';

function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Parse a `yyyy-MM-dd` string (or Date) into a local-midnight Date, without UTC day shifts. */
function toLocalDate(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function sameDay(a: Date | null, b: Date | null): boolean {
  return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Unified flight search widget.
 *
 * One component renders both the desktop search bar (>= 992px) and the mobile
 * grouped field list (< 992px), and is used on the home page (`variant="hero"`)
 * and the results page (`variant="results"`, sticky + condensed on scroll, and
 * inside the mobile "Modify search" sheet).
 *
 * Search state (origin, destination, dates, travellers, trip type) is kept here
 * and fed by the existing desktop pickers (app-from / app-dropdown /
 * app-passengers) and the mobile full-screen pickers, which all talk through
 * the shared services. Whichever picker emitted last wins, so the two layouts
 * never disagree.
 */
@Component({
  selector: 'app-flight-search-bar',
  templateUrl: './flight-search-bar.component.html',
  styleUrls: ['./flight-search-bar.component.scss'],
  standalone: false
})
export class FlightSearchBarComponent implements OnInit, OnChanges, OnDestroy {
  /** `hero`: home page (popular routes + payment note). `results`: booking page (compact). */
  @Input() variant: 'hero' | 'results' = 'hero';
  /** An existing search (the booking page's `bookingdetails`) to pre-populate the widget with. */
  @Input() prefill: Partial<FlightSearchPayload> | null = null;
  /** Results page: collapse everything but the bar while it is stuck to the top. */
  @Input() condensed = false;
  @Output() submitted = new EventEmitter<FlightSearchPayload>();

  @HostBinding('class.variant-results') get hostIsResults(): boolean { return this.variant === 'results'; }
  @HostBinding('class.is-condensed') get hostIsCondensed(): boolean { return this.condensed; }

  readonly form: UntypedFormGroup;
  readonly popularRoutes = POPULAR_ROUTES;
  readonly minDate = startOfToday();
  readonly fromPlaceholder = 'City or airport';
  readonly toPlaceholder = 'City or airport';

  fromData: FlightSearchAirport | null = null;
  destinationData: FlightSearchAirport | null = null;
  returning = false;
  passengers: FlightSearchPassengers = { adults: 1, children: 0, infants: 0, travelClass: 'economy' };
  submitAttempted = false;
  shaking = false;
  busyRoute: string | null = null;

  @ViewChild('returnPicker') private returnPicker?: MatDatepicker<Date>;

  private readonly destroy$ = new Subject<void>();
  private lastPrefillKey = '';
  /** Set when the results page asks for a new return date: search again as soon as one is picked. */
  private submitOnReturnPick = false;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly fromService: FromStatusService,
    private readonly destinationService: DropdownStatusService,
    private readonly passengerService: PassengerService,
    private readonly dataService: DataService,
    private readonly stateService: StateServiceService,
    private readonly searchUrl: SearchUrlService,
    private readonly openpanel: OpenpanelService,
    private readonly faro: FaroService
  ) {
    this.form = this.fb.group({
      pickup: [null],
      dropoff: [null],
      traveldate: [startOfToday()],
      returndate: [null]
    });
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    // The home page always opens on One way. The only exception is coming back
    // from a mobile picker page mid-search, where the chosen trip type must survive.
    if (this.variant === 'hero' && sessionStorage.getItem(PICKER_PENDING_KEY) !== '1') {
      this.stateService.setRoundTrip(false);
    }

    // Desktop pickers.
    this.fromService.valueChange.pipe(takeUntil(this.destroy$)).subscribe((airport: any) => this.applyFrom(airport));
    this.destinationService.deatinationValue.pipe(takeUntil(this.destroy$)).subscribe((airport: any) => this.applyTo(airport));
    this.passengerService.valueChange.pipe(takeUntil(this.destroy$)).subscribe((pax: any) => this.applyPassengers(pax));

    // Mobile full-screen pickers.
    this.fromService.valueMobileChange.pipe(takeUntil(this.destroy$)).subscribe((airport: any) => this.applyFrom(airport));
    this.destinationService.dropoffValue.pipe(takeUntil(this.destroy$)).subscribe((airport: any) => this.applyTo(airport));
    this.passengerService.valueMobileChange.pipe(takeUntil(this.destroy$)).subscribe((pax: any) => this.applyPassengers(pax));
    this.dataService.traveldateMobileChange.pipe(takeUntil(this.destroy$)).subscribe(value => {
      const date = toLocalDate(value);
      if (date) {
        this.form.patchValue({ traveldate: date });
        this.clampReturnDate();
      }
    });
    this.dataService.returndateMobileChange.pipe(takeUntil(this.destroy$)).subscribe(value => {
      const date = toLocalDate(value);
      if (date) {
        this.form.patchValue({ returndate: date });
      }
    });
    this.stateService.isRoundTrip$.pipe(takeUntil(this.destroy$)).subscribe(isRoundTrip => {
      this.returning = isRoundTrip;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['prefill'] && this.prefill) {
      this.applyPrefill(this.prefill);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  get departDate(): Date | null {
    return toLocalDate(this.form.value.traveldate);
  }

  get returnDate(): Date | null {
    return toLocalDate(this.form.value.returndate);
  }

  get returnMinDate(): Date {
    return this.departDate ?? this.minDate;
  }

  get totalPassengers(): number {
    return this.passengers.adults + this.passengers.children + this.passengers.infants;
  }

  get cabinLabel(): string {
    const key = (this.passengers.travelClass || 'economy').toLowerCase().replace(/[^a-z]/g, '');
    return CABIN_LABELS[key] ?? this.capitalize(this.passengers.travelClass);
  }

  get passengerSummary(): string {
    const total = this.totalPassengers;
    return `${total} ${total === 1 ? 'Traveller' : 'Travellers'} · ${this.cabinLabel}`;
  }

  get fromLabel(): string {
    return this.airportLabel(this.fromData);
  }

  get toLabel(): string {
    return this.airportLabel(this.destinationData);
  }

  get errors() {
    const from = this.fromData;
    const to = this.destinationData;
    const depart = this.departDate;
    const ret = this.returnDate;
    return {
      from: !from,
      to: !to,
      sameAirport: !!from && !!to && from.iata === to.iata,
      depart: !depart,
      return: this.returning && !ret,
      dateOrder: this.returning && !!ret && !!depart && ret.getTime() < depart.getTime()
    };
  }

  get isValid(): boolean {
    const e = this.errors;
    return !e.from && !e.to && !e.sameAirport && !e.depart && !e.return && !e.dateOrder;
  }

  get showErrors(): boolean {
    return this.submitAttempted && !this.isValid;
  }

  get validationHint(): string {
    const e = this.errors;
    if (e.from) { return 'Choose where you are flying from.'; }
    if (e.to) { return 'Choose where you are flying to.'; }
    if (e.sameAirport) { return 'Departure and destination airports must be different.'; }
    if (e.depart) { return 'Add a departure date.'; }
    if (e.return) { return 'Add a return date, or switch to one way.'; }
    if (e.dateOrder) { return 'Your return date must be on or after your departure date.'; }
    return 'Please review the highlighted fields.';
  }

  formatDay(date: Date | null): string {
    return date ? formatDate(date, 'EEE, d MMM', LOCALE) : '';
  }

  trackRoute(_: number, route: PopularRoute): string {
    return `${route.from.iata}-${route.to.iata}`;
  }

  // ---------------------------------------------------------------------------
  // Trip type
  // ---------------------------------------------------------------------------

  setTripType(returning: boolean): void {
    if (this.returning === returning) {
      return;
    }
    this.returning = returning;
    this.stateService.setRoundTrip(returning);
  }

  /** Desktop: the ghost "Add return" field turns the trip into a round trip and opens the picker. */
  onReturnFieldClick(picker: MatDatepicker<Date>): void {
    if (!this.returning) {
      this.setTripType(true);
      // Let the field render in its active state before anchoring the popup to it.
      setTimeout(() => picker.open());
      return;
    }
    picker.open();
  }

  // ---------------------------------------------------------------------------
  // Airports
  // ---------------------------------------------------------------------------

  swap(): void {
    const from = this.fromData;
    const to = this.destinationData;
    if (!from && !to) {
      return;
    }
    this.fromData = to;
    this.destinationData = from;
    this.form.patchValue({ pickup: to?.iata ?? null, dropoff: from?.iata ?? null });
    if (to) {
      this.fromService.changeSelectedValue(to as any);
    }
    if (from) {
      this.destinationService.setDestination(from as any);
    }
  }

  private applyFrom(airport: any): void {
    if (!airport || !airport.iata) {
      return;
    }
    this.fromData = this.normalizeAirport(airport);
    this.form.patchValue({ pickup: this.fromData.iata });
  }

  private applyTo(airport: any): void {
    if (!airport || !airport.iata) {
      return;
    }
    this.destinationData = this.normalizeAirport(airport);
    this.form.patchValue({ dropoff: this.destinationData.iata });
  }

  private normalizeAirport(airport: any): FlightSearchAirport {
    return {
      name: airport.name ?? '',
      city: airport.city ?? airport.name ?? '',
      country: airport.country ?? '',
      iata: airport.iata
    };
  }

  private airportLabel(airport: FlightSearchAirport | null): string {
    if (!airport) {
      return '';
    }
    return airport.city ? `${airport.city} (${airport.iata})` : airport.iata;
  }

  // ---------------------------------------------------------------------------
  // Dates
  // ---------------------------------------------------------------------------

  onDepartChange(): void {
    this.clampReturnDate();
  }

  onReturnChange(): void {
    if (this.submitOnReturnPick) {
      this.submitOnReturnPick = false;
      this.submit('form');
    }
  }

  /** Results page "Change return": open the return calendar and re-run the search once a date is chosen. */
  openReturnPicker(): void {
    this.setTripType(true);
    this.submitOnReturnPick = true;
    setTimeout(() => this.returnPicker?.open());
  }

  /** Keep the return date from drifting before the departure date. */
  private clampReturnDate(): void {
    const depart = this.departDate;
    const ret = this.returnDate;
    if (depart && ret && ret.getTime() < depart.getTime()) {
      this.form.patchValue({ returndate: depart });
    }
  }

  // ---------------------------------------------------------------------------
  // Passengers
  // ---------------------------------------------------------------------------

  private applyPassengers(pax: any): void {
    if (!pax || typeof pax !== 'object' || pax.adultpassenger === undefined) {
      return;
    }
    const next: FlightSearchPassengers = {
      adults: Number(pax.adultpassenger) || 1,
      children: Number(pax.childpassenger) || 0,
      infants: Number(pax.infantpassenger) || 0,
      travelClass: pax.classType || pax.class || 'economy'
    };
    const current = this.passengers;
    // Only swap the object when something changed: app-passengers re-emits on
    // every `initial` change, so an unconditional assignment would loop.
    if (
      current.adults !== next.adults ||
      current.children !== next.children ||
      current.infants !== next.infants ||
      current.travelClass !== next.travelClass
    ) {
      this.passengers = next;
    }
  }

  // ---------------------------------------------------------------------------
  // Prefill (results page)
  // ---------------------------------------------------------------------------

  private applyPrefill(search: Partial<FlightSearchPayload>): void {
    const key = JSON.stringify([
      search.from_data?.iata, search.destination_data?.iata, search.departure_on, search.arrival_on,
      search.returning, search.adult_count, search.child_count, search.infant_count, search.travel_class
    ]);
    if (key === this.lastPrefillKey) {
      return;
    }
    this.lastPrefillKey = key;

    const from = search.from_data?.iata ? this.normalizeAirport(search.from_data) : null;
    const to = search.destination_data?.iata ? this.normalizeAirport(search.destination_data) : null;
    const returning = !!search.returning || search.trip_type === 'return';
    const depart = toLocalDate(search.departure_on) ?? startOfToday();
    const ret = returning ? toLocalDate(search.arrival_on) : null;

    this.fromData = from;
    this.destinationData = to;
    this.returning = returning;
    this.form.patchValue({
      pickup: from?.iata ?? null,
      dropoff: to?.iata ?? null,
      traveldate: depart,
      returndate: ret
    });
    this.passengers = {
      adults: Number(search.adult_count) || 1,
      children: Number(search.child_count) || 0,
      infants: Number(search.infant_count) || 0,
      travelClass: search.travel_class || 'economy'
    };
    this.submitAttempted = false;

    // Push the airports through the shared services so the desktop pickers
    // (which render their own value) show the same selection.
    this.stateService.setRoundTrip(returning);
    if (from) {
      this.fromService.changeSelectedValue(from as any);
    }
    if (to) {
      this.destinationService.setDestination(to as any);
    }

    if (sessionStorage.getItem(PICKER_PENDING_KEY) === '1' && this.mobileChannelsPopulated()) {
      // Back from a full-screen picker: its selection is newer than the stored search.
      this.replayMobileChannels();
    } else {
      // Authoritative search (fresh load, shared link, or a new search): line the
      // mobile channels up with it so a later picker round-trip starts from, and
      // returns to, this exact search. Empty channels mean the app was reloaded,
      // so any leftover picker flag is stale.
      sessionStorage.removeItem(PICKER_PENDING_KEY);
      this.syncMobileChannels(from, to, depart, ret);
    }
  }

  /** False after a full page load: the picker channels live in memory only. */
  private mobileChannelsPopulated(): boolean {
    let fromSet = false;
    this.fromService.valueMobileChange.pipe(take(1)).subscribe((airport: any) => (fromSet = !!(airport && airport.iata)));
    const toSet = !!(this.destinationService.dropoffDateSource.value && this.destinationService.dropoffDateSource.value.iata);
    const dateSet = !!this.dataService.travelValueMobile.value;
    return fromSet || toSet || dateSet;
  }

  /** Re-apply whatever the mobile pickers currently hold (BehaviorSubjects replay synchronously). */
  private replayMobileChannels(): void {
    this.fromService.valueMobileChange.pipe(take(1)).subscribe((airport: any) => this.applyFrom(airport));
    this.destinationService.dropoffValue.pipe(take(1)).subscribe((airport: any) => this.applyTo(airport));
    this.passengerService.valueMobileChange.pipe(take(1)).subscribe((pax: any) => this.applyPassengers(pax));
    this.dataService.traveldateMobileChange.pipe(take(1)).subscribe(value => {
      const date = toLocalDate(value);
      if (date) {
        this.form.patchValue({ traveldate: date });
        this.clampReturnDate();
      }
    });
    this.dataService.returndateMobileChange.pipe(take(1)).subscribe(value => {
      const date = toLocalDate(value);
      if (date) {
        this.form.patchValue({ returndate: date });
      }
    });
  }

  private syncMobileChannels(
    from: FlightSearchAirport | null,
    to: FlightSearchAirport | null,
    depart: Date | null,
    ret: Date | null
  ): void {
    if (from) {
      this.fromService.changeSelectedMobileValue(from as any);
    }
    if (to) {
      this.destinationService.setDropOff(to as any);
    }
    if (depart) {
      this.dataService.changeTravelMessage(formatDate(depart, 'yyyy-MM-dd', 'en-KE'));
    }
    this.dataService.changeReturnMessage(ret ? formatDate(ret, 'yyyy-MM-dd', 'en-KE') : '');
    this.passengerService.changeMobilePassengerValue({
      adultpassenger: this.passengers.adults,
      childpassenger: this.passengers.children,
      infantpassenger: this.passengers.infants,
      class: this.passengers.travelClass,
      classType: this.passengers.travelClass
    });
  }

  // ---------------------------------------------------------------------------
  // Popular routes (hero only)
  // ---------------------------------------------------------------------------

  searchPopular(route: PopularRoute): void {
    if (this.busyRoute) {
      return;
    }
    this.busyRoute = this.trackRoute(0, route);
    this.fromData = route.from;
    this.destinationData = route.to;
    this.form.patchValue({ pickup: route.from.iata, dropoff: route.to.iata });
    this.fromService.changeSelectedValue(route.from as any);
    this.destinationService.setDestination(route.to as any);

    // A popular-route shortcut for "today" is rarely what people mean; default to tomorrow.
    if (!this.departDate || sameDay(this.departDate, startOfToday())) {
      const tomorrow = startOfToday();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.form.patchValue({ traveldate: tomorrow });
      this.clampReturnDate();
    }
    this.submit('popular');
    this.busyRoute = null;
  }

  // ---------------------------------------------------------------------------
  // Mobile pickers
  // ---------------------------------------------------------------------------

  openFromSheet(): void {
    this.openSheet('/mobile-from');
  }

  openToSheet(): void {
    this.openSheet('/mobile-to');
  }

  openDepartSheet(): void {
    this.openSheet('/depature-date');
  }

  openReturnSheet(): void {
    this.setTripType(true);
    this.openSheet('/return-date');
  }

  openPassengersSheet(): void {
    this.openSheet('/mobile-passenger');
  }

  private openSheet(path: string): void {
    // The booking page uses this flag to skip re-running the search when the
    // picker navigates back.
    sessionStorage.setItem('inSubpageNav', '1');
    sessionStorage.setItem(PICKER_PENDING_KEY, '1');
    this.router.navigate([path]);
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  onSubmit(event: Event): void {
    event.preventDefault();
    this.submit('form');
  }

  private submit(source: 'form' | 'popular'): void {
    this.submitAttempted = true;
    if (!this.isValid) {
      this.pulseShake();
      return;
    }

    const from = this.fromData as FlightSearchAirport;
    const to = this.destinationData as FlightSearchAirport;
    const departure = formatDate(this.departDate as Date, 'yyyy-MM-dd', 'en-KE');
    const arrival = this.returning ? formatDate(this.returnDate as Date, 'yyyy-MM-dd', 'en-KE') : departure;

    const payload: FlightSearchPayload = {
      depature_city: from.iata,
      destination_city: to.iata,
      departure_on: departure,
      arrival_on: arrival,
      trip_type: this.returning ? 'return' : 'oneway',
      adult_count: this.passengers.adults,
      child_count: this.passengers.children,
      infant_count: this.passengers.infants,
      travel_class: this.passengers.travelClass,
      from_data: from,
      destination_data: to,
      returning: this.returning
    };

    this.dataService.changeMessage(Base64.encode(JSON.stringify(payload)));
    sessionStorage.removeItem('inSubpageNav');
    sessionStorage.removeItem(PICKER_PENDING_KEY);
    localStorage.removeItem('modifySearchOpen');

    this.openpanel.track('flight_search_submitted', {
      pickup: payload.depature_city,
      dropoff: payload.destination_city,
      travel_date: payload.departure_on,
      return_date: this.returning ? payload.arrival_on : undefined,
      trip_type: payload.trip_type,
      adults: payload.adult_count,
      children: payload.child_count,
      infants: payload.infant_count,
      travel_class: payload.travel_class,
      source: this.variant,
      trigger: source
    });
    this.faro.pushEvent('search_submitted', {
      departure_city: payload.depature_city,
      arrival_city: payload.destination_city,
      departure_date: payload.departure_on,
      is_return: payload.returning,
      source: this.variant
    });

    this.submitted.emit(payload);
    const routeSlug = this.searchUrl.buildRouteSlug(from.city || from.iata, to.city || to.iata);
    this.router.navigate(['/booking', routeSlug], { queryParams: this.searchUrl.buildQueryParams(payload) });
  }

  private pulseShake(): void {
    this.shaking = false;
    if (typeof document !== 'undefined') {
      void document.body.offsetHeight;
    }
    this.shaking = true;
    setTimeout(() => (this.shaking = false), 320);
  }

  private capitalize(value: string): string {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }
}
